import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, ChevronRight, Database, KeyRound, RefreshCw, Unplug } from "lucide-react";
import { adminFetch, authenticatedFetch } from "../services/api";
import { classifyEndpointResult, getWorldEndpoints, summarizeEndpointResults } from "./nexusData";
import { Button } from "./ui";

type WorldId = "pulse" | "intelligence" | "capital" | "portfolio" | "agents" | "operations" | "learning" | "proof" | "expert";
type EndpointState = "checking" | "live" | "partial" | "empty" | "unavailable" | "rate_limited" | "auth_expired" | "auth_forbidden" | "degraded" | "timeout" | "error";
type EndpointResult = {
  id: string;
  label: string;
  path: string;
  description: string;
  state: EndpointState;
  status: number;
  data: unknown;
  error: string | null;
  meta: Record<string, unknown>;
};

// These two routes are owner-only (not public) — they require the caller's
// own Supabase Bearer token via authenticatedFetch(), same as every other
// endpoint in this panel. They only need a dedicated branch because
// authenticatedFetch() (fail-closed, always same-origin) is the correct
// dispatcher for them, while adminFetch() is reserved for /api/admin/*
// proxy routes specifically (see src/services/api.js's routing invariant).
function isCriticalSummaryPath(path: string) {
  return path === "/api/frontend-critical-summary" || path === "/api/frontend-critical-summary/readiness";
}

export type NexusWorldData = {
  results: EndpointResult[];
  summary: ReturnType<typeof summarizeEndpointResults>;
  loading: boolean;
  updatedAt: string | null;
  refresh: () => Promise<void>;
};

// `enabled: false` keeps the hook's return shape stable (no fetch, no
// interval, empty results) instead of skipping the call — Rules of Hooks
// forbid calling `useNexusWorldData` conditionally, so callers that already
// have this world's data from elsewhere (see `HqsCommandCenter`'s dedicated
// always-on "pulse" instance, reused by `<LiveWorldPanel/>` instead of
// fetching the same seven endpoints a second time) pass `sharedData`
// instead and set `enabled: false` here to avoid a redundant network round
// trip while still satisfying the hook-call-count invariant.
export function useNexusWorldData(world: WorldId, options: { enabled?: boolean; autoLoad?: boolean } = {}): NexusWorldData {
  const enabled = options.enabled !== false;
  const autoLoad = options.autoLoad !== false;
  const endpoints = useMemo(() => getWorldEndpoints(world), [world]);
  const [results, setResults] = useState<EndpointResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  // Bumped on every world switch and every refresh() call. A late-resolving
  // fetch from a *previous* area (e.g. "pulse") captures the generation that
  // was current when it started; if the area has since changed, its
  // generation is stale by the time the response arrives and must never be
  // allowed to overwrite the now-current area's diagnostic cards.
  const generationRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    const generation = ++generationRef.current;
    if (!endpoints.length) {
      setResults([]);
      setLoading(false);
      setUpdatedAt(null);
      return;
    }
    setLoading(true);
    // Keep the last completed snapshot readable during a refresh.
    setResults((previous) => previous.length ? previous : endpoints.map((endpoint) => ({ ...endpoint, state: "checking", status: 0, data: null, error: null, meta: {} })));
    const settled = await Promise.all(endpoints.map(async (endpoint) => {
      try {
        const response = isCriticalSummaryPath(endpoint.path)
          ? await authenticatedFetch(endpoint.path, { cache: "no-store" }, 12_000)
          : await adminFetch(endpoint.path, 25_000, { cache: "no-store" });
        return {
          ...endpoint,
          state: classifyEndpointResult(response) as EndpointState,
          status: response.status || 0,
          data: response.data ?? null,
          error: response.error ?? null,
          meta: response.meta || {},
        };
      } catch (error) {
        return {
          ...endpoint, state: "error" as const, status: 0, data: null, meta: {},
          error: error instanceof Error ? error.message : "Daten konnten nicht geladen werden.",
        };
      }
    }));
    // A newer refresh (this world's own re-fetch, or a switch to a different
    // world) has started in the meantime — discard this now-stale result
    // instead of writing it into the current area's state.
    if (generationRef.current !== generation) return;
    setResults(settled);
    setUpdatedAt(new Date().toISOString());
    setLoading(false);
  }, [endpoints, enabled]);

  useEffect(() => {
    if (!enabled) {
      generationRef.current += 1;
      setResults([]);
      setLoading(false);
      setUpdatedAt(null);
      return;
    }
    // Immediately invalidate any in-flight refresh from the previously
    // active area and clear its loading/diagnostic state before this area's
    // own fetch is scheduled, so no stale card can flash or persist.
    generationRef.current += 1;
    setResults([]);
    setLoading(false);
    setUpdatedAt(null);
    const timer = autoLoad ? window.setTimeout(() => void refresh(), 0) : undefined;
    return () => {
      window.clearTimeout(timer);
      generationRef.current += 1;
    };
  }, [refresh, enabled, autoLoad]);

  const summary = useMemo(() => summarizeEndpointResults(results), [results]);
  return { results, summary, loading, updatedAt, refresh };
}

const STATE_COPY: Record<EndpointState, { label: string; icon: typeof CheckCircle2 }> = {
  checking: { label: "WIRD GELADEN", icon: RefreshCw },
  live: { label: "LIVE", icon: CheckCircle2 },
  partial: { label: "TEILWEISE VERFÜGBAR", icon: AlertTriangle },
  empty: { label: "LEER", icon: Database },
  unavailable: { label: "NICHT VERFÜGBAR", icon: Unplug },
  // fix/pnl-dossier-mapping-and-live-truth: a 429 means the route exists and
  // works, it is only rate-limited right now — this used to be folded into
  // "unavailable" ("NICHT DEPLOYED"), a factually wrong label for a live,
  // working, merely-throttled endpoint (production incident:
  // /api/admin/alerts/summary).
  rate_limited: { label: "RATE-LIMITED", icon: AlertTriangle },
  auth_expired: { label: "ANMELDUNG ABGELAUFEN", icon: KeyRound },
  auth_forbidden: { label: "KEINE BESITZERBERECHTIGUNG", icon: KeyRound },
  degraded: { label: "KONFIGURATION GESTÖRT", icon: AlertTriangle },
  timeout: { label: "ZEITÜBERSCHREITUNG", icon: AlertTriangle },
  error: { label: "FEHLER", icon: AlertTriangle },
};

// Reuses the existing endpoint-contract--{auth,error} CSS treatment (no new
// styles) for the newer, more specific states — only the label/icon above
// differ, not the visual color coding.
function visualStateClass(state: EndpointState) {
  if (state === "partial") return "partial";
  if (state === "auth_expired" || state === "auth_forbidden") return "auth";
  if (state === "degraded" || state === "timeout" || state === "rate_limited") return "error";
  return state;
}

export function LiveWorldPanel({ world, sharedData }: { world: WorldId; sharedData?: NexusWorldData }) {
  // When the caller already holds this world's data (currently only
  // HqsCommandCenter's always-on "pulse" instance — the same canonical
  // seven-endpoint fetch the cockpit itself normalizes), reuse it instead of
  // firing a second, independent fetch of the identical endpoints. This is
  // what guarantees the diagnostic cards and the cockpit can never disagree
  // about the SAME seven pulse checks: they render from one fetch, not two.
  const ownData = useNexusWorldData(world, { enabled: !sharedData });
  const { results, summary, loading, updatedAt, refresh } = sharedData ?? ownData;
  if (!results.length && world === "expert") return null;
  // Only a genuinely live, non-empty payload counts toward coverage — a
  // null/empty response is tracked separately (see the "leer" badge below)
  // and must never inflate a "% live" figure.
  const coverage = summary.total ? Math.round((summary.live / summary.total) * 100) : 0;
  const gestoert = summary.auth_expired + summary.auth_forbidden + summary.degraded + summary.timeout + summary.error + summary.rate_limited;

  return (
    <section className="live-world-panel liquid-panel owner-data-panel" aria-label="Datenlage des aktuellen Bereichs" aria-busy={loading}>
      <header className="live-world-panel__head">
        <div>
          <span className="section-kicker">DATENLAGE · AKTUELLER BEREICH</span>
          <h2>{updatedAt ? <><strong>{summary.live} von {summary.total}</strong> Datenquellen vollständig verfügbar</> : loading ? "Daten werden geladen …" : "Noch keine Daten abgerufen"}</h2>
          <p>
            {loading && updatedAt ? "Aktualisierung läuft. Angezeigt wird der zuletzt abgeschlossene Abruf." : updatedAt ? `${coverage}% der angefragten Quellen liefern vollständige Daten. Einzelheiten findest du in der Diagnose.` : "Der Status erscheint nach dem ersten Abruf."}
          </p>
        </div>
        <div className="live-world-panel__summary">
          <span className="live">{summary.live} live</span>
          <span className="open">{summary.partial} teilweise</span>
          <span className="empty">{summary.empty} leer</span>
          <span className="open">{summary.unavailable} nicht verfügbar</span>
          <span className="error">{gestoert} gestört</span>
          <Button variant="outline" size="icon-sm" onClick={() => void refresh()} disabled={loading} aria-label="Datenwelt neu laden"><RefreshCw className={loading ? "spin" : ""} /></Button>
        </div>
      </header>
      <details className="owner-data-diagnostics">
      <summary><Database aria-hidden="true"/><span>Datenquellen und Diagnose</span><small>{summary.total} Quellen</small><ChevronRight aria-hidden="true"/></summary>
      <div className="live-world-panel__grid">
        {results.map((result) => {
          const stateInfo = STATE_COPY[result.state] ?? STATE_COPY.error;
          const Icon = stateInfo.icon;
          const durationMs = typeof result.meta?.frontendRequestDurationMs === "number" ? result.meta.frontendRequestDurationMs : null;
          const timeoutMs = typeof result.meta?.frontendProxyTimeoutMs === "number" ? result.meta.frontendProxyTimeoutMs : null;
          return (
            <details className={`endpoint-contract endpoint-contract--${visualStateClass(result.state)}`} key={result.id}>
              <summary>
                <span className="endpoint-contract__icon"><Icon className={result.state === "checking" ? "spin" : ""} /></span>
                <span><strong>{result.label}</strong><small>{result.description}</small></span>
                <em>HTTP {result.status || "—"} · {stateInfo.label}</em>
                <ChevronRight />
              </summary>
              <div>
                <code>{result.path}</code>
                {result.error ? <p>{result.error}</p> : null}
                {result.state === "timeout" && (durationMs != null || timeoutMs != null) ? (
                  <p>Gemessene Dauer: {durationMs != null ? `${durationMs} ms` : "unbekannt"} · Zeitlimit: {timeoutMs != null ? `${timeoutMs} ms` : "unbekannt"}</p>
                ) : null}
                <pre>{JSON.stringify(result.data, null, 2).slice(0, 6000) || "Keine Nutzdaten geliefert."}</pre>
              </div>
            </details>
          );
        })}
      </div>
      </details>
      <footer>Letzter Abruf: {updatedAt ? new Intl.DateTimeFormat("de-DE", { dateStyle: "short", timeStyle: "medium", timeZone: "Europe/Berlin" }).format(new Date(updatedAt)) : "—"} · Zeit in Berlin · Teilweise verfügbare und leere Antworten zählen nicht als vollständig.</footer>
    </section>
  );
}
